import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { getHeroContent } from '../../../services/heroContentService';

const FEATURES = [
  {
    id: 'form-builder',
    label: 'Form Builder',
    icon: 'LayoutTemplate',
    headline: 'Build any form in minutes',
    description: 'Drag-and-drop fields, conditional logic, multi-step flows, and custom themes — no code needed.',
    mockup: {
      title: 'Contact Form — Builder',
      badge: { text: 'Draft', color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
      content: (
        <div className="space-y-2.5">
          {[
            { label: 'Full Name', type: 'text', active: false },
            { label: 'Email Address', type: 'email', active: true },
            { label: 'Phone Number', type: 'tel', active: false },
            { label: 'Message', type: 'textarea', active: false },
          ]?.map((field, i) => (
            <div key={i} className={`rounded-lg border px-3 py-2.5 text-xs flex items-center justify-between ${field?.active ? 'border-[#0e7572] bg-teal-50 ring-1 ring-[#0e7572]/20' : 'border-gray-200 bg-gray-50'}`}>
              <span className={field?.active ? 'text-[#0e7572] font-medium' : 'text-gray-500'}>{field?.label}</span>
              {field?.active && <span className="text-[10px] px-1.5 py-0.5 bg-[#0e7572] text-white rounded font-medium">Selected</span>}
            </div>
          ))}
          <div className="mt-3 px-4 py-2 bg-[#0e7572] rounded-lg text-xs text-white font-semibold text-center cursor-pointer hover:bg-[#0a5f5c] transition-colors">
            Submit →
          </div>
          <div className="flex items-center gap-2 pt-1">
            <div className="flex items-center gap-1 text-[10px] text-gray-400"><Icon name="Plus" size={10} />Add field</div>
            <div className="flex items-center gap-1 text-[10px] text-gray-400"><Icon name="GitBranch" size={10} />Add logic</div>
            <div className="flex items-center gap-1 text-[10px] text-gray-400"><Icon name="Palette" size={10} />Theme</div>
          </div>
        </div>
      ),
    },
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: 'BarChart3',
    headline: 'See exactly what\'s working',
    description: 'Real-time submission tracking, conversion funnels, device breakdowns, and geographic insights.',
    mockup: {
      title: 'Form Analytics — Last 30 days',
      badge: { text: 'Live', color: 'bg-green-50 border-green-200 text-green-700' },
      content: (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Views', value: '8,420', trend: '+12%', up: true },
              { label: 'Submissions', value: '2,847', trend: '+23%', up: true },
              { label: 'Conversion', value: '33.8%', trend: '-2%', up: false },
            ]?.map((s, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                <div className="text-sm font-bold text-gray-900">{s?.value}</div>
                <div className="text-[10px] text-gray-500">{s?.label}</div>
                <div className={`text-[10px] font-medium mt-0.5 ${s?.up ? 'text-green-600' : 'text-red-500'}`}>{s?.trend}</div>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[10px] text-gray-500 mb-1.5 font-medium">Submissions over time</div>
            <div className="flex items-end gap-1 h-16">
              {[22, 38, 30, 55, 48, 70, 62, 80, 74, 90, 85, 95, 88, 100]?.map((h, i) => (
                <div key={i} className="flex-1 rounded-sm transition-all" style={{ height: `${h}%`, background: i >= 11 ? '#0e7572' : `rgba(14,117,114,${0.12 + i * 0.05})` }} />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-gray-500">
            <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#0e7572]" />Desktop 62%</div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-teal-300" />Mobile 31%</div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-gray-300" />Tablet 7%</div>
          </div>
        </div>
      ),
    },
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: 'CreditCard',
    headline: 'Collect payments inside your forms',
    description: 'Accept one-time or recurring payments, set pricing rules, and manage transactions — all in one place.',
    mockup: {
      title: 'Payment Form — Checkout',
      badge: { text: 'Secure', color: 'bg-blue-50 border-blue-200 text-blue-700' },
      content: (
        <div className="space-y-2.5">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            <div className="text-[10px] text-gray-500 mb-1">Order Summary</div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-700">Pro Plan — Monthly</span>
              <span className="text-xs font-bold text-gray-900">$49.00</span>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 px-3 py-2.5 text-xs text-gray-500 bg-gray-50">Card number</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-gray-200 px-3 py-2.5 text-xs text-gray-500 bg-gray-50">MM / YY</div>
            <div className="rounded-lg border border-[#0e7572] px-3 py-2.5 text-xs text-[#0e7572] bg-teal-50 font-medium">CVC</div>
          </div>
          <div className="px-4 py-2.5 bg-[#0e7572] rounded-lg text-xs text-white font-semibold text-center flex items-center justify-center gap-1.5">
            <Icon name="Lock" size={11} />Pay $49.00 securely
          </div>
          <div className="flex items-center justify-center gap-2 text-[10px] text-gray-400">
            <Icon name="Shield" size={10} />SSL encrypted · Powered by Stripe
          </div>
        </div>
      ),
    },
  },
  {
    id: 'submissions',
    label: 'Submissions',
    icon: 'Inbox',
    headline: 'Manage every response in one inbox',
    description: 'Filter, label, export, and act on submissions. Get notified instantly when new responses arrive.',
    mockup: {
      title: 'Submissions Inbox',
      badge: { text: '12 new', color: 'bg-teal-50 border-teal-200 text-[#0e7572]' },
      content: (
        <div className="space-y-1.5">
          {[
            { name: 'Sarah Johnson', form: 'Contact Form', time: '2m ago', new: true },
            { name: 'Mark Davis', form: 'Quote Request', time: '15m ago', new: true },
            { name: 'Emily Chen', form: 'Event RSVP', time: '1h ago', new: false },
            { name: 'James Wilson', form: 'Contact Form', time: '3h ago', new: false },
          ]?.map((sub, i) => (
            <div key={i} className={`flex items-center gap-2.5 rounded-lg px-3 py-2 border ${sub?.new ? 'bg-teal-50/50 border-teal-100' : 'bg-white border-gray-100'}`}>
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">
                {sub?.name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-800 truncate">{sub?.name}</div>
                <div className="text-[10px] text-gray-400">{sub?.form}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {sub?.new && <div className="w-1.5 h-1.5 rounded-full bg-[#0e7572]" />}
                <span className="text-[10px] text-gray-400">{sub?.time}</span>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1 text-[10px] text-gray-400">
            <span>Showing 4 of 2,847</span>
            <span className="text-[#0e7572] cursor-pointer hover:underline">View all →</span>
          </div>
        </div>
      ),
    },
  },
];

const hexToRgba = (hex, opacity) => {
  const normalized = /^#[0-9a-fA-F]{6}$/.test(hex || '') ? hex : '#007876';
  const r = parseInt(normalized?.slice(1, 3), 16);
  const g = parseInt(normalized?.slice(3, 5), 16);
  const b = parseInt(normalized?.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${Math.min(100, Math.max(0, Number(opacity) || 0)) / 100})`;
};

const safeText = (value, fallback = '', maxLength = 1000) => (
  typeof value === 'string' ? value?.slice(0, maxLength) : fallback
);

const safeHttpsUrl = (value) => (
  typeof value === 'string' && /^https:\/\//i.test(value) ? value?.slice(0, 2000) : ''
);

const normalizeSavedFeatures = (tabs) => {
  if (!Array.isArray(tabs)) return [];
  const seenIds = new Set();

  return tabs?.slice(0, 20)?.flatMap((tab, index) => {
    if (!tab || typeof tab !== 'object' || Array.isArray(tab)) return [];
    const rawId = safeText(tab?.id, `hero-section-${index + 1}`, 80);
    const id = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/.test(rawId)
      ? rawId
      : `hero-section-${index + 1}`;
    if (seenIds?.has(id)) return [];
    seenIds?.add(id);

    const builtIn = FEATURES?.find(feature => feature?.id === id);
    return [{
      ...(builtIn || {}),
      id,
      label: safeText(tab?.label, `Section ${index + 1}`, 80),
      icon: safeText(tab?.icon, 'Star', 80),
      headline: safeText(tab?.headline, '', 160),
      description: safeText(tab?.description, '', 1000),
      mediaType: tab?.mediaType === 'video' ? 'video' : 'image',
      mediaUrl: safeHttpsUrl(tab?.mediaUrl),
      mediaAlt: safeText(tab?.mediaAlt, '', 300),
      mockup: builtIn?.mockup || {
        title: safeText(tab?.label, `Section ${index + 1}`, 80),
        badge: { text: 'Feature', color: 'bg-teal-50 border-teal-200 text-[#0e7572]' },
        content: null,
      },
    }];
  });
};

const HeroSection = () => {
  const [activeTab, setActiveTab] = useState('form-builder');
  const [savedContent, setSavedContent] = useState(null);

  useEffect(() => {
    let active = true;
    getHeroContent()
      ?.then(content => {
        if (!active || !content?.tabs?.length) return;
        setSavedContent(content);
        setActiveTab(current => content?.tabs?.some(tab => tab?.id === current)
          ? current
          : content?.tabs?.[0]?.id);
      })
      ?.catch(error => {
        console.warn('Using built-in hero content because saved content could not be loaded:', error);
      });
    return () => {
      active = false;
    };
  }, []);

  const savedFeatures = normalizeSavedFeatures(savedContent?.tabs);
  const features = savedFeatures?.length ? savedFeatures : FEATURES;
  const activeFeature = features?.find(f => f?.id === activeTab) || features?.[0];
  const handleFeatureKeyDown = (event, index) => {
    const lastIndex = features?.length - 1;
    let nextIndex;
    if (event?.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event?.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event?.key === 'Home') nextIndex = 0;
    if (event?.key === 'End') nextIndex = lastIndex;
    if (nextIndex === undefined) return;
    event?.preventDefault();
    const nextFeature = features?.[nextIndex];
    setActiveTab(nextFeature?.id);
    document?.getElementById(`${nextFeature?.id}-tab`)?.focus();
  };
  const background = savedContent?.background && typeof savedContent?.background === 'object'
    ? savedContent?.background
    : {};
  const gradientDirections = ['to bottom', 'to top', 'to right', 'to left', 'to bottom right', 'to bottom left'];
  const gradientDirection = gradientDirections?.includes(background?.gradientDirection)
    ? background?.gradientDirection
    : 'to bottom';
  const gradientFrom = /^#[0-9a-fA-F]{6}$/.test(background?.gradientFrom || '')
    ? background?.gradientFrom
    : '#ffffff';
  const gradientTo = /^#[0-9a-fA-F]{6}$/.test(background?.gradientTo || '')
    ? background?.gradientTo
    : '#007876';
  const solidColor = /^#[0-9a-fA-F]{6}$/.test(background?.solidColor || '')
    ? background?.solidColor
    : '#ffffff';
  const backgroundMediaUrl = safeHttpsUrl(background?.mediaUrl);
  const sectionBackground = background?.type === 'solid'
    ? solidColor
    : background?.type === 'image' && backgroundMediaUrl
      ? `url(${backgroundMediaUrl}) center/cover no-repeat`
      : background?.type === 'gradient'
        ? `linear-gradient(${gradientDirection}, ${gradientFrom} 0%, ${hexToRgba(gradientTo, background?.gradientToOpacity)} 100%)`
        : 'linear-gradient(to bottom, #ffffff 0%, rgba(0,120,118,0.5) 100%)';

  return (
    <section
      className="pt-24 sm:pt-28 pb-0 overflow-hidden relative"
      style={{
        background: sectionBackground,
        backgroundColor: '#ffffff',
      }}
    >
      {/* Remove secondary glow overlay */}
      <div className="absolute inset-0 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Top badge */}
        <div className="flex justify-center mb-6 sm:mb-7">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#007876]/20 bg-[#007876]/8 text-sm font-medium text-[#007876] backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-[#007876] animate-pulse" />
            <span>Built for marketing, sales, and operations</span>
          </div>
        </div>

        {/* Headline */}
        <div className="text-center max-w-4xl mx-auto mb-5 sm:mb-6">
          <h1 className="font-heading font-bold leading-[1.08] tracking-tight text-gray-900">
            <span className="block text-4xl sm:text-5xl lg:text-7xl">The form builder for</span>
            <span className="block text-4xl sm:text-5xl lg:text-7xl" style={{ color: '#007876' }}>marketing teams, freelancers, &amp; agencies</span>
          </h1>
        </div>

        {/* Subheadline */}
        <p className="text-center text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto mb-7 sm:mb-9 leading-relaxed px-2">
          Build, manage, test, and optimize forms that convert — with visible submission limits, real-time analytics, built-in payments, and powerful integrations.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
          <Link
            to="/authentication"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3.5 text-white font-semibold text-sm rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl touch-manipulation"
            style={{ backgroundColor: '#007876' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#005f5d'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#007876'}
          >
            <span>Start building for free</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
          <Link
            to="/templates-gallery"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-7 py-3.5 border border-gray-300 hover:border-[#007876]/50 text-gray-700 hover:text-[#007876] font-semibold text-sm rounded-xl transition-all duration-200 bg-white hover:bg-[#007876]/5 active:bg-[#007876]/10 touch-manipulation"
          >
            <Icon name="LayoutTemplate" size={16} />
            <span>Browse templates</span>
          </Link>
        </div>

        {/* Trust micro-copy */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 mb-10 sm:mb-12 text-sm text-gray-500">
          {[
            { icon: 'BadgeDollarSign', text: 'Free plan available' },
            { icon: 'Gauge', text: 'Plan limits shown up front' },
            { icon: 'Shield', text: 'Cancel anytime' },
          ]?.map(({ icon, text }) => (
            <div key={text} className="flex items-center gap-1.5">
              <Icon name={icon} size={14} className="text-gray-400" />
              <span>{text}</span>
            </div>
          ))}
        </div>

        {/* Feature showcase: one continuous product surface, with the selected tab
            visually becoming the label for the story beneath it. */}
        <div className="flex flex-col overflow-hidden rounded-2xl sm:rounded-3xl border border-[#007876]/20 bg-white/90 shadow-[0_24px_70px_rgba(14,117,114,0.14)] backdrop-blur-sm">
          {/* Compact segmented navigation: the active tab and action share one elevated
              surface so the showcase reads as a single, intentional product control. */}
          <aside id="platform-feature-tabs" className="relative z-10 order-1 border-b border-[#007876]/10 bg-[#eaf7f5]/80 p-2.5 sm:p-3 lg:p-4" aria-label="Platform features">
            <div className="flex flex-col gap-2.5 rounded-[1.15rem] border border-white/90 bg-white/90 p-1.5 shadow-[0_10px_28px_rgba(14,117,114,0.10)] backdrop-blur-md sm:flex-row sm:items-center sm:gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Feature showcase">
                {features?.map((feature, index) => {
                  const isActive = activeTab === feature?.id;
                  return (
                    <button
                      key={feature?.id}
                      id={`${feature?.id}-tab`}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls="feature-showcase-panel"
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => setActiveTab(feature?.id)}
                      onKeyDown={(event) => handleFeatureKeyDown(event, index)}
                      className={`group relative flex min-h-[43px] shrink-0 items-center gap-2 rounded-[0.85rem] px-3 text-left text-xs font-semibold transition-all duration-200 ease-out touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007876] focus-visible:ring-offset-1 sm:min-h-[46px] sm:px-3.5 sm:text-sm ${
                        isActive
                          ? 'bg-[#dff2ef] text-[#075f5c] shadow-[inset_0_0_0_1px_rgba(14,117,114,0.13),0_3px_8px_rgba(14,117,114,0.10)]'
                          : 'bg-[#f1faf8] text-[#075f5c] hover:bg-[#e4f5f2]'
                      }`}
                    >
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-200 sm:h-8 sm:w-8 ${
                        isActive ? 'bg-[#007876] text-white shadow-sm' : 'bg-[#eff8f6] text-[#007876] group-hover:bg-[#dff2ef]'
                      }`}>
                        <Icon name={feature?.icon} size={15} />
                      </span>
                      <span>{feature?.label}</span>
                      <Icon name="ArrowUpRight" size={13} className={`ml-0.5 transition-all duration-200 ${isActive ? 'translate-x-0 opacity-100' : '-translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`} />
                    </button>
                  );
                })}
              </div>
              <Link
                to="/authentication"
                className="group flex min-h-[43px] shrink-0 items-center justify-center gap-2 rounded-[0.85rem] bg-[#0e7771] px-4 text-xs font-bold text-white shadow-[0_4px_12px_rgba(14,119,113,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#0b6964] hover:shadow-[0_7px_16px_rgba(14,119,113,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007876] focus-visible:ring-offset-2 active:translate-y-0 sm:min-h-[46px] sm:px-5 sm:text-sm"
              >
                <span>Try it free</span>
                <Icon name="ArrowRight" size={14} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </aside>

          {/* Mockup area */}
          <div
            id="feature-showcase-panel"
            role="tabpanel"
            aria-labelledby={`${activeFeature?.id}-tab`}
            className="relative z-0 order-2 min-w-0 flex flex-col bg-white overflow-hidden"
          >
            <div className="border-b border-gray-100 px-5 py-5 sm:px-7 sm:py-6 lg:px-10">
              <h2 className="text-lg sm:text-xl font-semibold leading-tight text-gray-900">{activeFeature?.headline}</h2>
              <p className="text-sm text-gray-600 leading-relaxed mt-2">{activeFeature?.description}</p>
            </div>
            <div className="flex-1 p-4 sm:p-6 lg:p-10 bg-gradient-to-b from-gray-50/50 to-transparent">
              <div className="max-w-4xl mx-auto h-full flex flex-col justify-center">
              {/* Saved media or built-in product mockup */}
              {activeFeature?.mediaUrl ? (
                <div className="rounded-xl sm:rounded-2xl overflow-hidden border border-gray-200 bg-white">
                  {activeFeature?.mediaType === 'video' ? (
                    <video
                      src={activeFeature?.mediaUrl}
                      aria-label={activeFeature?.mediaAlt || activeFeature?.headline}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      className="block w-full max-h-[520px] object-contain bg-black"
                    />
                  ) : (
                    <img
                      src={activeFeature?.mediaUrl}
                      alt={activeFeature?.mediaAlt || activeFeature?.headline || activeFeature?.label}
                      className="block w-full max-h-[520px] object-contain bg-white"
                    />
                  )}
                </div>
              ) : (
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-2xl border border-white/10">
                {/* Browser bar */}
                <div className="bg-white/95 px-3 sm:px-5 py-2 sm:py-3 flex items-center gap-2 sm:gap-3 border-b border-gray-100">
                  <div className="flex gap-1 sm:gap-1.5">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 bg-gray-100 rounded-lg border border-gray-200 text-[10px] sm:text-xs text-gray-500 font-mono max-w-[200px] sm:max-w-none overflow-hidden">
                      <Icon name="Lock" size={9} className="text-[#007876] flex-shrink-0" />
                      <span className="truncate">webforms.biz/form-builder</span>
                    </div>
                  </div>
                </div>

                {/* App content */}
                <div className="bg-white p-3 sm:p-5 lg:p-8">
                  <div className="grid grid-cols-12 gap-3 sm:gap-4">
                    {/* Sidebar — hidden on mobile */}
                    <div className="col-span-2 hidden lg:flex flex-col gap-1.5">
                      <div className="h-7 bg-gray-100 rounded-lg mb-2" />
                      {['LayoutDashboard', 'FileText', 'Inbox', 'BarChart3', 'Zap', 'Settings']?.map((icon, i) => (
                        <div key={i} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg ${i === 0 ? 'bg-teal-50 border border-teal-100' : ''}`}>
                          <Icon name={icon} size={13} className={i === 0 ? 'text-[#007876]' : 'text-gray-300'} />
                          <div className={`h-1.5 rounded flex-1 ${i === 0 ? 'bg-teal-200' : 'bg-gray-100'}`} />
                        </div>
                      ))}
                    </div>

                    {/* Main content */}
                    <div className="col-span-12 lg:col-span-10">
                      {/* Panel title bar */}
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <div className="text-xs sm:text-sm font-semibold text-gray-800">{activeFeature?.mockup?.title}</div>
                        <div className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full border text-[10px] sm:text-xs font-medium ${activeFeature?.mockup?.badge?.color}`}>
                          <div className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                          {activeFeature?.mockup?.badge?.text}
                        </div>
                      </div>
                      {/* Dynamic mockup content */}
                      <div className="max-w-full sm:max-w-sm">
                        {activeFeature?.mockup?.content}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default HeroSection;